import _extends from "@babel/runtime/helpers/extends";
import { RecommendationLabel } from '@wix/ambassador-premium-domains-v1-recommendation/types';
import { Box, Loader } from '@wix/design-system';
import React, { useState } from 'react';
import { PricingSection } from '../Pricing/PricingSection';
import { SearchRecommendationModule } from './SearchRecommendation.module';
import { DomainSearchSuiteModule } from '../../module/domainSearchSuite.module';
import { PremiumDomainBadge } from '../PremiumDomainBadge/PremiumDomainBadge';
import { parseDomain } from '@wix/domains-serverless-interface';
import s from './SearchRecommendation.module.scss';
import { AiInsightsButton } from '../Insight/AiInsightsButton';
import { MobileAwareDisabledExplainer } from '../MobileAwareDisabledExplainer';
const SCALED_TITLE_MIN_FONT_SIZE = '0.75rem';
const SCALED_TITLE_MAX_FONT_SIZE = '1.125rem';
export const SearchRecommendation = props => {
  const {
    label,
    primary = false,
    primaryBadgeOnly = false,
    active = false,
    domain,
    disabled = false,
    premium = false,
    price,
    onButtonClick,
    dataHook,
    onMouseEnter,
    onAiInsightsClick,
    useMobileLayout = false,
    isLeanView = false,
    useCompactSuggestionRowStyle = false,
    showLabelBadge = true,
    pricingOptimized,
    isPricingLoading = false,
    compactPriceLabel = false,
    titleOverflowStrategy = 'ellipsis'
  } = props;
  const scalesTitle = titleOverflowStrategy === 'scale';
  const pricingIsOptimized = pricingOptimized ?? (isLeanView || useCompactSuggestionRowStyle);
  const [isClickLoading, setIsClickLoading] = useState(false);
  const {
    t
  } = DomainSearchSuiteModule.internals.useTranslations();
  const {
    SearchRecommendationContainer,
    GetItButtonContainer,
    ActionsContainer,
    GetItButton,
    TitleDisplay,
    LabelContainer,
    DomainInfoContainer,
    Label,
    MobileActions,
    Footer,
    InlineExtras,
    DomainNote
  } = SearchRecommendationModule.SDK.Layouts;
  const hasLabel = label && label !== RecommendationLabel.UNKNOWN_LABEL;
  const showLabel = hasLabel && showLabelBadge;
  const tld = parseDomain(domain ?? '').tld;
  const handleGetItClick = async () => {
    setIsClickLoading(true);
    try {
      await (onButtonClick == null ? void 0 : onButtonClick(props));
    } finally {
      setIsClickLoading(false);
    }
  };
  const getItButton = /*#__PURE__*/React.createElement(MobileAwareDisabledExplainer, {
    disabled: disabled,
    content: t('domain_search_suite.error.domain_manager_permissions.tooltip'),
    tooltipProps: {
      inline: true,
      size: 'medium',
      appendTo: 'scrollParent',
      zIndex: 99999
    }
  }, /*#__PURE__*/React.createElement(GetItButton, {
    disabled: disabled || isClickLoading,
    domainName: domain,
    priority: useCompactSuggestionRowStyle ? 'secondary' : active ? 'primary' : 'secondary',
    className: useCompactSuggestionRowStyle ? s.optimizedGetIt : undefined,
    onClick: handleGetItClick,
    dataHook: `${dataHook}-button`
  }, isClickLoading ? /*#__PURE__*/React.createElement(Loader, {
    size: "tiny"
  }) : t('domain_search_suite.domain_gate.suggestions.card.cta')));
  const labelContainerContent = /*#__PURE__*/React.createElement(LabelContainer, {
    premium: premium,
    visible: showLabel || premium,
    domain: domain ?? '',
    label: label
  }, showLabel && /*#__PURE__*/React.createElement(Label, {
    primary: primary || primaryBadgeOnly,
    label: label,
    domain: domain ?? ''
  }), premium && /*#__PURE__*/React.createElement(PremiumDomainBadge, null));
  const showPrimaryAppearance = primary && !primaryBadgeOnly;
  const isExactMatch = label === RecommendationLabel.EXACT_MATCH;
  const titleDisplay = /*#__PURE__*/React.createElement(TitleDisplay, {
    title: domain ?? '',
    primary: showPrimaryAppearance,
    exactMatch: isExactMatch,
    isLeanView: isLeanView,
    useCompactTitleStyle: useCompactSuggestionRowStyle,
    showComBrRestriction: useCompactSuggestionRowStyle && tld === 'com.br',
    overflowStrategy: titleOverflowStrategy,
    minFontSize: SCALED_TITLE_MIN_FONT_SIZE,
    maxFontSize: SCALED_TITLE_MAX_FONT_SIZE
  });
  return /*#__PURE__*/React.createElement("div", {
    onMouseEnter: onMouseEnter,
    style: {
      height: '100%',
      ...(useMobileLayout ? {
        width: '100%',
        boxSizing: 'border-box'
      } : {})
    }
  }, /*#__PURE__*/React.createElement(SearchRecommendationContainer, _extends({
    dataHook: dataHook
  }, useMobileLayout ? {
    direction: 'vertical',
    padding: 'SP4',
    gap: 'SP4',
    align: 'left',
    verticalAlign: 'top',
    className: `${s.mobileCard}${showPrimaryAppearance ? ` ${s.mobilePrimaryCard}` : ''}`
  } : useCompactSuggestionRowStyle ? {
    padding: '16px 24px'
  } : {}), useMobileLayout ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: useCompactSuggestionRowStyle ? '8px' : 'SP6',
    width: scalesTitle ? '100%' : undefined
  }, labelContainerContent, /*#__PURE__*/React.createElement(Box, {
    className: scalesTitle ? `${s.mobileDomainTitle} ${s.scaledDomainTitle}` : s.mobileDomainTitle
  }, titleDisplay)), /*#__PURE__*/React.createElement(MobileActions, {
    inline: useCompactSuggestionRowStyle,
    pricing: /*#__PURE__*/React.createElement(PricingSection, {
      pricing: price,
      tld: tld,
      domainName: domain,
      useMobileLayout: true,
      denseStrikethroughStyle: pricingIsOptimized,
      useIntroPriceLabelCopy: compactPriceLabel && pricingIsOptimized,
      matchStrikethroughSize: pricingIsOptimized,
      isPricingLoading: isPricingLoading,
      exactMatch: isExactMatch,
      compactPriceLabel: compactPriceLabel
    }),
    getItButton: getItButton,
    aiInsightsButton: !isLeanView && !useCompactSuggestionRowStyle && onAiInsightsClick ? /*#__PURE__*/React.createElement(AiInsightsButton, {
      onClick: () => onAiInsightsClick(domain ?? '')
    }) : undefined
  })) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    direction: "horizontal",
    align: "left",
    gap: "SP3",
    flex: scalesTitle ? 1 : undefined,
    minWidth: scalesTitle ? 0 : undefined
  }, /*#__PURE__*/React.createElement(DomainInfoContainer, {
    flex: scalesTitle ? 1 : undefined,
    minWidth: scalesTitle ? 0 : undefined
  }, !useCompactSuggestionRowStyle && labelContainerContent, /*#__PURE__*/React.createElement(Box, {
    direction: "horizontal",
    verticalAlign: "middle",
    gap: useCompactSuggestionRowStyle ? 'SP2' : 'SP3'
  }, scalesTitle ? /*#__PURE__*/React.createElement(Box, {
    flex: 1,
    minWidth: 0,
    className: s.scaledDomainTitle
  }, titleDisplay) : titleDisplay, useCompactSuggestionRowStyle && labelContainerContent, /*#__PURE__*/React.createElement(InlineExtras, {
    domain: domain,
    active: active
  })), /*#__PURE__*/React.createElement(DomainNote, {
    domain: domain,
    price: price,
    exactMatch: isExactMatch
  }))), /*#__PURE__*/React.createElement(ActionsContainer, {
    flexShrink: scalesTitle ? 0 : undefined
  }, /*#__PURE__*/React.createElement(PricingSection, {
    pricing: price,
    tld: tld,
    domainName: domain,
    denseStrikethroughStyle: pricingIsOptimized,
    useIntroPriceLabelCopy: compactPriceLabel && pricingIsOptimized,
    isPricingLoading: isPricingLoading,
    exactMatch: isExactMatch,
    compactPriceLabel: compactPriceLabel
  }), /*#__PURE__*/React.createElement(GetItButtonContainer, null, getItButton)))), !useMobileLayout && /*#__PURE__*/React.createElement(Footer, {
    domain: domain,
    active: active
  }));
};
//# sourceMappingURL=SearchRecommendation.js.map